package com.devsusana.portfolio.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.w3c.dom.HTMLLinkElement

/**
 * Layout specifically crafted for technical blog posts.
 * Inherits the global portfolio scaffold (PageLayout) with sticky navigation & footer,
 * provides a streamlined back arrow to the blog, and renders the article in an
 * ergonomically styled reading container with tight, polished vertical pacing.
 */
@Layout
@Composable
fun ArticleLayout(content: @Composable () -> Unit) {
    val colorMode = ColorMode.current

    // No external CSS required - all styled via native Silk and Compose HTML components

    PageLayout(title = "Blog | Susana Córdoba") {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(960.px)
                .padding(top = 0.cssRem, bottom = 2.5.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(0.5.cssRem)
        ) {
            // Subtle, minimalist back arrow
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 2.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Link(
                    path = "/#blog",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = Modifier.cursor(Cursor.Pointer)
                ) {
                    Row(
                        modifier = Modifier
                            .size(32.px)
                            .borderRadius(8.px)
                            .backgroundColor(
                                if (colorMode.isDark) Color.rgba(255, 255, 255, 0.05f)
                                else Color.rgba(0, 0, 0, 0.04f)
                            )
                            .border(1.px, LineStyle.Solid, SitePalette.surfaceBorder(colorMode)),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center
                    ) {
                        SpanText(
                            text = "←",
                            modifier = Modifier
                                .fontSize(1.15.cssRem)
                                .fontWeight(FontWeight.Bold)
                                .color(if (colorMode.isDark) SitePalette.androidGreen else Color.rgb(0x04, 0x78, 0x57))
                        )
                    }
                }
            }

            // Main Article Body Card
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .minWidth(0.px)
                    .borderRadius(16.px)
                    .styleModifier {
                        property("padding", "clamp(1rem, 3vw, 2rem)")
                    }
                    .classNames("article-wrapper", "article-content", if (colorMode.isDark) "theme-dark" else "theme-light")
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .minWidth(0.px)
                ) {
                    content()
                }
            }
        }
    }
}
